/**
 * importLinearSemantic — the Linear runtime consumer.
 *
 * Structurally identical to importNotionSemantic (that's the point): read a
 * Linear team or project semantically, build the snapshot, hand it to the
 * shared apply path. Recovers issue relations (blocks/related/duplicate), the
 * sub-issue tree, and label/state category — the data the flat import drops.
 * Then every item it created gets the issue's mapped fields (the description,
 * the priority into "Priority"…) through the same routine linking one item
 * uses, so an import and a link never disagree on what comes over.
 */

import { loadFieldMappingConfig, getColorRulesForEntity } from '../fieldMappingRegistry';
import { applyLinearValuesToItem, type LinearSyncResult } from '../linearItemSync';
import { linearImportSemantic } from './linearSemanticApi';
import { buildLinearSnapshot } from './linearSnapshot';
import { applySemanticImport, type SemanticImportResult } from './applySemanticImport';

export interface LinearImportResult extends SemanticImportResult {
  /** Items whose mapped fields (description, Priority…) came over. */
  fieldsApplied: number;
}

export async function importLinearTeamSemantic(params: {
  workspaceId: string;
  /** At least one of teamId / projectId. */
  teamId?: string;
  projectId?: string;
  userId?: string;
  groupIntoCollection?: boolean;
}): Promise<{ data: LinearImportResult | null; error: string | null }> {
  const { workspaceId, teamId, projectId, userId, groupIntoCollection = true } = params;
  if (!teamId && !projectId) return { data: null, error: 'Choose a Linear team or project to import.' };

  let read;
  try {
    read = await linearImportSemantic(workspaceId, { teamId, projectId });
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Linear read failed' };
  }

  const config = await loadFieldMappingConfig(workspaceId, 'linear');
  const snapshot = buildLinearSnapshot(read, {
    relationshipMap: config.relationshipMap,
    typeMap: config.typeMap,
    colorRules: getColorRulesForEntity(config.miroColorMaps ?? [], 'items'),
  });

  const externalUrlByExternalId = new Map(read.issues.map(i => [i.externalId, i.externalUrl]));
  const applied = await applySemanticImport(snapshot, {
    workspaceId,
    connectorType: 'linear',
    sourceLabel: 'Linear import',
    sourceTag: 'linear-import',
    proposedByAgent: 'linear_import',
    containerTitle: read.team.name,
    externalUrlByExternalId,
    userId,
    groupIntoCollection,
  });

  // The mapped fields, item by item — the same routine as linking one item.
  const data: LinearImportResult = { ...applied, fieldsApplied: 0 };
  const issuesByExternalId = new Map(read.issues.map(i => [i.externalId, i]));
  for (const [externalId, itemId] of Object.entries(applied.createdByExternalId)) {
    const issue = issuesByExternalId.get(externalId);
    if (!issue) continue;
    const values = issue.attributes ?? { title: issue.title, description: issue.description ?? '' };
    let result: LinearSyncResult;
    try {
      result = await applyLinearValuesToItem({
        workspaceId,
        itemId,
        userId,
        reason: 'import',
        issue: {
          id: externalId,
          identifier: String(values.identifier ?? externalId),
          url: issue.externalUrl ?? '',
          title: issue.title,
          updatedAt: String(values.updated_at ?? new Date().toISOString()),
          teamKey: typeof values.team === 'string' ? values.team : undefined,
        },
        values,
      });
    } catch (err) {
      data.errors.push(`Fields for “${issue.title}”: ${err instanceof Error ? err.message : 'not applied'}`);
      continue;
    }
    if (result.wrote.length > 0) data.fieldsApplied++;
    data.errors.push(...result.errors.map(e => `Fields for “${issue.title}”: ${e}`));
  }
  return { data, error: null };
}
